import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../api/client";

const BLOOD_TYPES = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

function strength(pw) {
  if (!pw) return { score: 0, label: "", tone: "" };
  let score = 0;
  if (pw.length >= 8) score += 1;
  if (pw.length >= 12) score += 1;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;

  if (score <= 2) return { score, label: "Weak", tone: "var(--es-bad)" };
  if (score === 3) return { score, label: "Fair", tone: "var(--es-warn)" };
  if (score === 4) return { score, label: "Good", tone: "var(--es-teal-600)" };
  return { score, label: "Strong", tone: "var(--es-ok)" };
}

export default function Sign_UP() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "",
    bloodType: "", password: "", confirmPassword: "",
  });
  const [showPw, setShowPw] = useState(false);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const pwStrength = useMemo(() => strength(form.password), [form.password]);

  function validate() {
    const next = {};
    if (!form.firstName.trim()) next.firstName = "Enter your first name.";
    if (!form.lastName.trim()) next.lastName = "Enter your last name.";
    if (!form.email.trim()) next.email = "Enter your email address.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email))
      next.email = "That doesn't look like a valid email address.";
    if (!form.bloodType) next.bloodType = "Choose a blood type.";
    if (!form.password) next.password = "Choose a password.";
    else if (form.password.length < 8) next.password = "Use at least 8 characters.";
    if (form.password !== form.confirmPassword)
      next.confirmPassword = "The two passwords do not match.";
    return next;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setBanner(null);

    const found = validate();
    setErrors(found);
    if (Object.keys(found).length) return;

    setLoading(true);
    try {
      const response = await api.post("/users/register", {
        firstname: form.firstName.trim(),
        lastname: form.lastName.trim(),
        email: form.email.trim(),
        bloodType: form.bloodType,
        password: form.password,
        confirmPassword: form.confirmPassword,
      });

      // Sign-in is refused until the address is confirmed, so say so here
      // rather than dropping the user on a form that will reject them.
      navigate("/signin", {
        state: {
          justRegistered: true,
          email: form.email.trim(),
          notice:
            response.data?.message ||
            "Account created. Check your email to verify your address.",
        },
      });
    } catch (err) {
      // error.response is absent on network failures, so it cannot be
      // dereferenced unguarded.
      setBanner({
        kind: "error",
        text:
          err.response?.data?.message ||
          "We couldn't create your account. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="es-auth">
      <aside className="es-auth__aside">
        <div>
          <Link to="/" className="es-brand" style={{ color: "#fff" }}>
            <span className="es-brand__mark" aria-hidden="true">ES</span>
            <span className="es-brand__name">
              <strong>Edu</strong>
              <span style={{ color: "var(--es-sand-400)" }}>Special</span>
            </span>
          </Link>
        </div>

        <div>
          <h2 className="es-auth__aside-title">Start with an assessment, not a waiting list</h2>
          <p>
            Creating an account lets you register for programmes, keep your
            details in one place and hear about events before they open
            publicly.
          </p>
        </div>

        <p style={{ fontSize: "0.85rem", color: "#a9c5c2" }}>
          We only use your details to deliver and coordinate your support.
        </p>
      </aside>

      <div className="es-auth__panel">
        <div className="es-auth__form es-rise">
          <Link to="/" className="es-auth__back">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m15 18-6-6 6-6" />
            </svg>
            Back to site
          </Link>

          <h1 className="es-h1" style={{ fontSize: "1.9rem" }}>Create your account</h1>
          <p className="es-lede" style={{ marginBottom: "1.5rem", fontSize: "0.98rem" }}>
            It takes about a minute.
          </p>

          {banner && (
            <div className="es-alert es-alert--error" role="alert">{banner.text}</div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="es-row">
              <div className="es-field">
                <label className="es-label" htmlFor="su-first">First name</label>
                <input id="su-first" className="es-input" value={form.firstName}
                  onChange={set("firstName")} autoComplete="given-name"
                  aria-invalid={errors.firstName ? "true" : undefined}
                  aria-describedby={errors.firstName ? "su-first-err" : undefined} />
                {errors.firstName && <p className="es-error" id="su-first-err">{errors.firstName}</p>}
              </div>

              <div className="es-field">
                <label className="es-label" htmlFor="su-last">Last name</label>
                <input id="su-last" className="es-input" value={form.lastName}
                  onChange={set("lastName")} autoComplete="family-name"
                  aria-invalid={errors.lastName ? "true" : undefined}
                  aria-describedby={errors.lastName ? "su-last-err" : undefined} />
                {errors.lastName && <p className="es-error" id="su-last-err">{errors.lastName}</p>}
              </div>
            </div>

            <div className="es-field">
              <label className="es-label" htmlFor="su-email">Email address</label>
              <input id="su-email" type="email" className="es-input" value={form.email}
                onChange={set("email")} autoComplete="email"
                aria-invalid={errors.email ? "true" : undefined}
                aria-describedby={errors.email ? "su-email-err" : undefined} />
              {errors.email && <p className="es-error" id="su-email-err">{errors.email}</p>}
            </div>

            <div className="es-field">
              <label className="es-label" htmlFor="su-blood">Blood type</label>
              <select id="su-blood" className="es-select" value={form.bloodType}
                onChange={set("bloodType")}
                aria-invalid={errors.bloodType ? "true" : undefined}
                aria-describedby={errors.bloodType ? "su-blood-err" : undefined}>
                <option value="">Choose blood type</option>
                {BLOOD_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
              {errors.bloodType && <p className="es-error" id="su-blood-err">{errors.bloodType}</p>}
            </div>

            <div className="es-field">
              <label className="es-label" htmlFor="su-pw">Password</label>
              <div style={{ position: "relative" }}>
                <input id="su-pw" type={showPw ? "text" : "password"} className="es-input"
                  value={form.password} onChange={set("password")} autoComplete="new-password"
                  style={{ paddingRight: "4.25rem" }}
                  aria-invalid={errors.password ? "true" : undefined}
                  aria-describedby={errors.password ? "su-pw-err" : "su-pw-strength"} />
                <button type="button" className="es-linkbtn"
                  onClick={() => setShowPw((v) => !v)}
                  aria-pressed={showPw}
                  style={{ position: "absolute", right: "0.75rem", top: "50%", transform: "translateY(-50%)" }}>
                  {showPw ? "Hide" : "Show"}
                </button>
              </div>

              {errors.password ? (
                <p className="es-error" id="su-pw-err">{errors.password}</p>
              ) : (
                <p className="es-hint" id="su-pw-strength" aria-live="polite">
                  {form.password ? (
                    <>
                      Strength:{" "}
                      <strong style={{ color: pwStrength.tone }}>{pwStrength.label}</strong>
                      {" · at least 8 characters"}
                    </>
                  ) : (
                    "At least 8 characters."
                  )}
                </p>
              )}
            </div>

            <div className="es-field">
              <label className="es-label" htmlFor="su-pw2">Confirm password</label>
              <input id="su-pw2" type={showPw ? "text" : "password"} className="es-input"
                value={form.confirmPassword} onChange={set("confirmPassword")}
                autoComplete="new-password"
                aria-invalid={errors.confirmPassword ? "true" : undefined}
                aria-describedby={errors.confirmPassword ? "su-pw2-err" : undefined} />
              {errors.confirmPassword && (
                <p className="es-error" id="su-pw2-err">{errors.confirmPassword}</p>
              )}
            </div>

            <button type="submit" className="es-btn es-btn--primary"
              style={{ width: "100%" }} disabled={loading}>
              {loading ? "Creating your account…" : "Create account"}
            </button>

            <p className="es-hint" style={{ textAlign: "center" }}>
              We&rsquo;ll email you a link to verify your address before you can
              sign in.
            </p>
          </form>

          <p className="es-auth__switch">
            Already have an account? <Link to="/signin">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
